import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { ethers } from 'ethers';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useRejectionDeadlineDays } from '../hooks/useAuditingPoolConfig';
import { useRequest } from '../hooks/useRequests';
import {
  useMilestoneSchedule,
  useMilestoneReviewProgress,
  useMilestoneDeliverables,
  useMilestoneDsmbVetoed,
  useMilestoneDsmbVetoBy,
  useStageProof,
  useRequestMilestoneType,
  milestoneStateName,
  deliverableTypeName,
  DELIVERABLE_TYPES,
  MILESTONE_STATES,
  type MilestoneState,
} from '../hooks/useMilestones';
import { useIsActiveAuditor, useIsAuditorFor } from '../hooks/useAuditorsInPool';
import { useHasDsmbVeto, MILESTONE_TYPES } from '../hooks/useAuditorRegistry';
import { useBalanceOf } from '../hooks/useToken';
import { useWriteContract } from '../hooks/useWriteContract';

export default function Request() {
  const { id: idStr = '0' } = useParams();
  const id = Number.isFinite(Number(idStr)) ? Number(idStr) : 0;
  const account = useAccount();

  const req           = useRequest(id);
  const schedule      = useMilestoneSchedule(id);
  const stageProof    = useStageProof(id);
  const rejDays       = useRejectionDeadlineDays();
  const isActAud      = useIsActiveAuditor(account);
  const isAudForReq   = useIsAuditorFor(id, account);
  const balance       = useBalanceOf(account);
  // §3.1 — does this wallet have a DSMB-veto attestation in the registry?
  // Drives the destructive "DSMB veto" button below, shown only when the
  // current milestone is typed as ClinicalData.
  const hasDsmbVeto   = useHasDsmbVeto(account);

  const r = req.data;
  const isLab = !!account && !!r && r.labWallet.toLowerCase() === account.toLowerCase();
  const isAuditorForThis = !!isAudForReq.data;
  const isActiveAuditor = !!isActAud.data;
  const hasMethBalance = balance.data !== undefined && balance.data > 0n;

  const milestoneNames  = schedule.data?.names ?? [];
  const milestoneAmounts = schedule.data?.amounts ?? [];
  const milestoneStatuses = schedule.data?.statuses ?? [];
  const numMilestones = milestoneNames.length;
  const currentIdx = r ? Number(r.currentMilestone) : 0;
  const optionExerciseRequired = !!r?.optionExerciseRequired;

  const isCompleted = numMilestones > 0 && currentIdx >= numMilestones && !!r?.verdictAccepted && !r?.cancelled;

  const currentState: MilestoneState | null = useMemo(() => {
    if (isCompleted) return 'Approved';
    if (currentIdx >= numMilestones) return null;
    const stateNum = milestoneStatuses[currentIdx];
    return stateNum !== undefined ? milestoneStateName(stateNum) : null;
  }, [currentIdx, numMilestones, milestoneStatuses, isCompleted]);

  const totalBudgetWei = milestoneAmounts.reduce((s, a) => s + a, 0n);
  const disbursedWei = milestoneAmounts.slice(0, currentIdx).reduce((s, a) => s + a, 0n);

  // Review progress + DSMB veto state for the CURRENT milestone (the only one
  // an auditor can review right now).
  const reviewProgress = useMilestoneReviewProgress(id, currentIdx);
  const dsmbVetoed = useMilestoneDsmbVetoed(id, currentIdx);
  const dsmbVetoBy = useMilestoneDsmbVetoBy(id, currentIdx);
  const deliverables = useMilestoneDeliverables(id, currentIdx);
  // §3.2 — type of the current milestone. requestMilestoneTypes[reqId][idx]
  // returns 0/Generic for un-typed (legacy) requests, so the UI gracefully
  // degrades to "Generic" without surfacing an error.
  const currentMilestoneType = useRequestMilestoneType(id, currentIdx);
  const currentMilestoneTypeIdx = Number(currentMilestoneType.data ?? 0n);
  const isClinicalMilestone =
    currentMilestoneTypeIdx === MILESTONE_TYPES.indexOf('ClinicalData');

  // voteHype write wiring (§98).
  const { write: writeHype, status: hypeStatus } = useWriteContract();
  const hypeSubmitting = hypeStatus === 'submitting' || hypeStatus === 'pending';
  async function onVoteHype() {
    if (!hasMethBalance || hypeSubmitting) return;
    await writeHype({
      contract: 'auditingPool',
      fn: 'voteHype',
      args: [BigInt(id)],
      options: {
        pendingTitle: `Voting hype on #${id}…`,
        successTitle: `Hyped request #${id}`,
        invalidate: [['metha', 'auditingPool']],
      },
    });
  }

  return (
    <Page ctxLabel={`request #${id.toString().padStart(3, '0')}`} ctx="lab" title={`Metha · Request #${id}`} docsPath="how-it-works/requests" docsLabel="Research requests">
      <Crumbs crumbs={[
        { to: '/', label: 'Overview' },
        { to: '/auditing', label: 'Auditing' },
        { label: `Request #${id}` },
      ]} />

      {/* DSMB veto banner — only shows when the current milestone is vetoed */}
      {dsmbVetoed.data === true && (
        <div className="warn mb-14" style={{ borderColor: 'var(--red)', background: 'rgba(255,80,80,.05)' }}>
          <span className="mono" style={{ color: 'var(--red)', letterSpacing: 1.2, textTransform: 'uppercase', fontSize: 10, marginRight: 8 }}>DSMB VETO</span>
          The current milestone has been vetoed by{' '}
          <span className="mono" style={{ color: 'var(--ink)' }}>{dsmbVetoBy.data ? short(dsmbVetoBy.data) : '—'}</span>{' '}
          on patient-safety grounds. Auditors cannot approve until the lab resubmits proof.
        </div>
      )}

      {/* Editorial dossier header */}
      <div className="panel mb-14">
        <div className="dossier-head">
          <div className="kicker">
            REQUEST #{id.toString().padStart(3, '0')}
            {r && r.cancelled && ' · CANCELLED'}
            {r && isCompleted && ' · FINAL (all milestones approved)'}
            {r && r.verdictAccepted && !r.cancelled && !isCompleted && ' · APPROVED VERDICT'}
            {r && r.verdictCast && !r.verdictAccepted && !r.cancelled && ' · VERDICT REJECTED BY LAB'}
            {r && !r.verdictCast && ' · AUDITORS VOTING'}
            {r && numMilestones > 0 && !isCompleted && ` · MILESTONE ${Math.min(currentIdx + 1, numMilestones)} of ${numMilestones}`}
            {r && isCompleted && ` · ${numMilestones} of ${numMilestones}`}
            {optionExerciseRequired && ' · BARDA OPTION GATING'}
          </div>
          <h2>{r?.title || (req.isLoading ? 'Loading…' : '(untitled request)')}</h2>
          <div className="byline">
            <b>{r?.labWallet ? short(r.labWallet) : '—'}</b>
            {' · '}IPFS {r?.ipfsHash ? <a href="#" className="acc">{r.ipfsHash.slice(0, 12)}…</a> : '—'}
            <span style={{ margin: '0 6px', color: 'var(--line-3)' }}>·</span>
            disbursed {weiToEth(disbursedWei).toFixed(4)} / {weiToEth(totalBudgetWei).toFixed(4)} ETH
          </div>
        </div>
        {r?.description && (
          <div className="abstract">{r.description}</div>
        )}

        <div style={{ padding: '14px 28px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--line)', background: 'var(--panel-2)' }}>
          <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase' }}>Current state</span>
          {isCompleted ? (
            <Pill tone="green">Final · all {numMilestones} milestones approved</Pill>
          ) : currentState ? (
            <Pill tone={currentState === 'Disputed' ? 'amber' : currentState === 'Approved' ? 'green' : 'blue'}>
              M{currentIdx + 1} = {currentState}
            </Pill>
          ) : (
            <Pill tone="gray">{r?.verdictCast ? 'no milestones loaded' : 'pre-verdict'}</Pill>
          )}
          <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginLeft: 14 }}>You are</span>
          {isLab && <Pill tone="ctx">Lab (labWallet=msg.sender)</Pill>}
          {isAuditorForThis && <Pill tone="purple">Assigned auditor</Pill>}
          {isActiveAuditor && !isAuditorForThis && <Pill tone="purple">Active auditor</Pill>}
          {hasMethBalance && !isLab && <Pill tone="blue">Holder · {fmt.meth(weiToEth(balance.data!))} METH</Pill>}
          {!account && <Pill tone="gray">Wallet not connected</Pill>}
        </div>
      </div>

      {/* State machine */}
      <div className="panel mb-14">
        <div className="p-head">
          <h3>Milestone state machine · M{currentIdx + 1}</h3>
          <span className="src"><code>getMilestoneStatus({id}, {currentIdx})</code></span>
        </div>
        <div style={{ padding: '18px 24px' }}>
          <div className="fsm">
            {MILESTONE_STATES.map((s, i) => {
              const stateNum = MILESTONE_STATES.indexOf(currentState ?? 'Locked');
              const cls = currentState === s ? 'curr' : i < stateNum ? 'done' : '';
              return (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <span className={`st${cls ? ' ' + cls : ''}`}>{s}</span>
                  {i < MILESTONE_STATES.length - 1 && <span className="arr">→</span>}
                </span>
              );
            })}
          </div>
          <div className="mono dim mt-14" style={{ fontSize: 11, lineHeight: 1.7 }}>
            proof = <span className="acc">{stageProof.data && stageProof.data !== '0x' + '00'.repeat(32)
              ? short(stageProof.data)
              : '— not submitted'}</span><br />
            rejectionDeadlineDays = <span className="ink">
              {rejDays.data !== undefined ? `${Number(rejDays.data) / 86400} days` : '—'}
            </span> (configurable by governance)<br />
            verdict approve count = <span className="ink">
              {r ? `${Number(r.approveVotes)} of ${Number(r.totalVotes)}` : '—'}
            </span>
          </div>
        </div>
      </div>

      <div className="row r-2u1 mb-14">
        {/* Milestone schedule — uses §4.8 single-call view */}
        <div className="panel">
          <div className="p-head">
            <h3>Milestone schedule · {currentIdx} / {numMilestones} unlocked</h3>
            <span className="src"><code>getMilestoneSchedule({id})</code></span>
          </div>
          <div className="tbl">
            <div className="th" style={{ gridTemplateColumns: '50px 1fr 110px 90px' }}>
              <span>#</span><span>NAME</span><span>AMOUNT</span><span>STATUS</span>
            </div>
            {numMilestones === 0 && (
              <div className="p-body" style={{ padding: 18, color: 'var(--dim)', fontSize: 12 }}>
                No milestones loaded yet — verdict pending or rejected.
              </div>
            )}
            {milestoneNames.map((name, i) => {
              const stateNum = milestoneStatuses[i];
              const stateName = stateNum !== undefined ? milestoneStateName(stateNum) : null;
              const tone =
                stateName === 'Approved'       ? 'green'  :
                stateName === 'Disputed'       ? 'amber'  :
                stateName === 'ProofSubmitted' ? 'amber'  :
                stateName === 'Claimed'        ? 'blue'   :
                stateName === 'Unlocked'       ? 'blue'   :
                'gray';
              return (
                <div key={i} className="tr" style={{ gridTemplateColumns: '50px 1fr 110px 90px' }}>
                  <span className="pid">M{i + 1}</span>
                  <span className="who">{name}</span>
                  <span>{weiToEth(milestoneAmounts[i] ?? 0n).toFixed(4)} ETH</span>
                  <span><Pill tone={tone}>{stateName ?? '…'}</Pill></span>
                </div>
              );
            })}
          </div>

          {/* Review progress bar — only visible during active review */}
          {currentState === 'ProofSubmitted' && reviewProgress.data && (
            <div style={{ padding: '14px 18px', borderTop: '1px solid var(--line)', background: 'var(--panel-2)' }}>
              <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
                Review progress · M{currentIdx + 1}
              </div>
              <ReviewProgressBar
                approveCount={Number(reviewProgress.data.approveCount)}
                rejectCount={Number(reviewProgress.data.rejectCount)}
                total={Number(reviewProgress.data.totalAuditors)}
              />
            </div>
          )}

          {/* Typed deliverables for the current milestone (§3.2) */}
          {deliverables.data && deliverables.data.length > 0 && (
            <div style={{ padding: '14px 18px', borderTop: '1px solid var(--line)' }}>
              <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
                Deliverables · M{currentIdx + 1}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {deliverables.data.map((d, i) => (
                  <div key={i} className="mono" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pill tone="ctx">{deliverableTypeName(d.dtype) ?? '?'}</Pill>
                    <span style={{ color: 'var(--ink)' }}>{d.uri || '— no URI —'}</span>
                    <span className="dim">{short(d.evidenceHash)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="panel mb-14">
            <div className="p-head">
              <h3>Verdict & hype</h3>
              <span className="src"><code>requestList({id}).hype</code></span>
            </div>
            <div className="p-body">
              <div className="estim">
                <span>VERDICT</span>
                <b>{r?.verdictCast ? (r.verdictAccepted ? 'accepted' : 'rejected by lab') : 'pending'}</b>
              </div>
              <div className="estim">
                <span>APPROVE VOTES</span>
                <b>{r ? `${Number(r.approveVotes)} ✓ / ${Number(r.totalVotes)}` : '—'}</b>
              </div>
              <div className="estim">
                <span>HYPE (METH-weighted)</span>
                <b>{r ? fmt.meth(weiToEth(r.hype)) : '—'}</b>
              </div>
              <button
                className={`btn btn-block mt-14${!hasMethBalance || hypeSubmitting ? ' btn-locked' : ''}`}
                disabled={!hasMethBalance || hypeSubmitting}
                onClick={onVoteHype}
              >
                {hypeSubmitting ? 'Submitting…' :
                  hasMethBalance
                    ? `Vote hype (+${fmt.meth(weiToEth(balance.data ?? 0n))} METH)`
                    : account
                      ? 'No METH to weight hype'
                      : 'Connect wallet'}
              </button>
            </div>
          </div>

          <div className="panel">
            <div className="p-head"><h3>Deposits & bonds</h3><span className="src"><code>requestDeposits[lab]</code></span></div>
            <div className="p-body">
              <div style={{ marginBottom: 14 }}>
                <div className="mono" style={{ fontSize: 9, letterSpacing: 1.5, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 6 }}>● live · on-chain today</div>
                <div className="estim"><span>SUBMISSION DEPOSIT</span><b>0.005 ETH</b></div>
                <div className="estim"><span>STATE</span><b>{r?.cancelled ? 'cancellable / claimable' : 'locked while active'}</b></div>
              </div>
              <div style={{ paddingTop: 14, borderTop: '1px dashed var(--line)' }}>
                <div className="mono" style={{ fontSize: 9, letterSpacing: 1.5, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>○ phase 2 · not yet on-chain</div>
                <div className="estim" style={{ opacity: 0.7 }}><span>METH BOND</span><b>10–20% of funded</b></div>
                <div className="estim" style={{ opacity: 0.7 }}><span>SLASH RISK</span><b style={{ color: 'var(--amber)' }}>2/3 majority vote</b></div>
                <p className="help mt-14" style={{ marginBottom: 0 }}>
                  Whitepaper §4.5 Mechanism C — bond contract not deployed yet.
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Actions panel · role + state aware */}
      <ActionsPanel
        requestId={id}
        currentMilestoneIdx={currentIdx}
        isLab={isLab}
        isAuditorForThis={isAuditorForThis}
        isActiveAuditor={isActiveAuditor}
        hasMethBalance={hasMethBalance}
        verdictCast={!!r?.verdictCast}
        verdictAccepted={!!r?.verdictAccepted}
        cancelled={!!r?.cancelled}
        currentState={currentState}
        milestoneAmounts={milestoneAmounts}
        userHasDsmbVeto={!!hasDsmbVeto.data}
        isClinicalMilestone={isClinicalMilestone}
        milestoneAlreadyVetoed={!!dsmbVetoed.data}
      />
    </Page>
  );
}

/* -------------------- Actions panel -------------------- */

function ActionsPanel(p: {
  requestId: number;
  currentMilestoneIdx: number;
  isLab: boolean;
  isAuditorForThis: boolean;
  isActiveAuditor: boolean;
  hasMethBalance: boolean;
  verdictCast: boolean;
  verdictAccepted: boolean;
  cancelled: boolean;
  currentState: MilestoneState | null;
  milestoneAmounts: bigint[];
  /** §3.1 — wallet has hasDsmbVeto attestation in the registry. */
  userHasDsmbVeto: boolean;
  /** §3.1 — current milestone is typed as ClinicalData (the only kind a DSMB can veto). */
  isClinicalMilestone: boolean;
  /** §3.1 — current milestone is already DSMB-vetoed; hide the button to avoid wasted gas. */
  milestoneAlreadyVetoed: boolean;
}) {
  const id = p.requestId;
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const labCanAccept   = p.isLab && p.verdictCast && !p.verdictAccepted && !p.cancelled;
  const labCanClaim    = p.isLab && p.verdictAccepted && p.currentState === 'Unlocked';
  const labCanProof    = p.isLab && p.verdictAccepted && p.currentState === 'Claimed';
  const labCanResubmit = p.isLab && p.verdictAccepted && p.currentState === 'Disputed';
  const labCanReclaim  = p.isLab && (p.cancelled || (p.verdictCast && !p.verdictAccepted));

  const audCanCastVote = p.isAuditorForThis && !p.verdictCast;
  const audCanReview   = p.isAuditorForThis && p.currentState === 'ProofSubmitted';
  const audCanApply    = p.isActiveAuditor && !p.isAuditorForThis && !p.verdictCast;

  // §3.1 — show the DSMB veto button only when all preconditions are met and
  // the lab is past acceptVerdict (i.e. there's a milestone to veto). We allow
  // it from any active state (Unlocked / Claimed / ProofSubmitted / Disputed)
  // since the contract itself enforces the legal precondition.
  const canDsmbVeto =
    p.userHasDsmbVeto &&
    p.isClinicalMilestone &&
    !p.milestoneAlreadyVetoed &&
    p.verdictAccepted &&
    !p.cancelled &&
    (p.currentState === 'Unlocked' ||
     p.currentState === 'Claimed' ||
     p.currentState === 'ProofSubmitted' ||
     p.currentState === 'Disputed');

  // Form state — proof inputs.
  const [proofText, setProofText] = useState('');
  const [resubmitText, setResubmitText] = useState('');
  // §3.2 — repeater for typed deliverables. Default one empty row.
  const [deliverables, setDeliverables] = useState<Array<{ dtype: number; uri: string; evidenceHash: string }>>(
    [{ dtype: 0, uri: '', evidenceHash: '' }]
  );
  // Vote-cast form state.
  const [voteApprove, setVoteApprove] = useState<true | false>(true);
  const [voteScore, setVoteScore] = useState('80');
  // For amounts, prefill with the proposed amounts (formatted as ETH).
  // Initial value derived from props; subsequent updates flow through
  // useEffect so we never call setState during render (which crashes the
  // page after a successful tx invalidates the schedule cache).
  const [voteAmounts, setVoteAmounts] = useState<string[]>(
    () => p.milestoneAmounts.map((a) => weiToEth(a).toFixed(4))
  );
  // Keep voteAmounts in sync when the schedule shape changes (e.g. after a
  // tx invalidates the cache and getMilestoneSchedule returns a new array).
  // Stringify the comparison so React deps work; don't drop user edits if
  // the shape is unchanged.
  const proposedKey = p.milestoneAmounts.map((a) => weiToEth(a).toFixed(4)).join('|');
  useEffect(() => {
    setVoteAmounts((prev) => {
      const next = proposedKey ? proposedKey.split('|') : [];
      if (prev.length === next.length) return prev; // preserve user edits
      return next;
    });
  }, [proposedKey]);

  const proofBytes32 = toBytes32(proofText);
  const resubmitBytes32 = toBytes32(resubmitText);

  function doWrite(args: { fn: string; args: unknown[]; pendingTitle: string; successTitle: string }) {
    return () =>
      write({
        contract: 'auditingPool',
        fn: args.fn,
        args: args.args,
        options: {
          pendingTitle: args.pendingTitle,
          successTitle: args.successTitle,
          invalidate: [['metha', 'auditingPool']],
        },
      });
  }

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Actions</h3>
        <span className="src">role + state aware · only the buttons your wallet can call are enabled</span>
      </div>

      {/* LAB */}
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Pill tone="ctx">Lab</Pill>
          <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            {p.isLab ? 'labWallet === msg.sender' : 'visible only to labWallet'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <button className={`btn ${labCanAccept && !submitting ? 'btn-prim' : 'btn-locked'}`}
                  disabled={!labCanAccept || submitting}
                  onClick={doWrite({ fn: 'acceptVerdict', args: [BigInt(id)], pendingTitle: 'Accepting verdict…', successTitle: 'Verdict accepted' })}>
            Accept verdict
          </button>
          <button className={`btn ${labCanAccept && !submitting ? '' : 'btn-locked'}`}
                  disabled={!labCanAccept || submitting}
                  onClick={doWrite({ fn: 'rejectVerdict', args: [BigInt(id)], pendingTitle: 'Rejecting verdict…', successTitle: 'Verdict rejected' })}>
            Reject verdict
          </button>
          <span style={{ width: 1, height: 18, background: 'var(--line-2)', margin: '0 4px' }} />
          <button className={`btn ${labCanClaim && !submitting ? 'btn-prim' : 'btn-locked'}`}
                  disabled={!labCanClaim || submitting}
                  onClick={doWrite({ fn: 'claimCurrentMilestone', args: [BigInt(id)], pendingTitle: 'Claiming milestone…', successTitle: 'Milestone claimed' })}>
            Claim current milestone
          </button>
        </div>

        {labCanProof && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <input
                className="mono"
                style={{ flex: 1, padding: '6px 10px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
                placeholder="proof reference (text or 0x-prefixed bytes32) — legacy single-anchor path"
                value={proofText}
                onChange={(e) => setProofText(e.target.value)}
              />
              <button className={`btn ${proofBytes32 && !submitting ? 'btn-prim' : 'btn-locked'}`}
                      disabled={!proofBytes32 || submitting}
                      onClick={doWrite({ fn: 'requestNextStage', args: [BigInt(id), proofBytes32 ?? '0x' + '0'.repeat(64)], pendingTitle: 'Submitting proof…', successTitle: 'Proof submitted' })}>
                Submit proof
              </button>
            </div>

            {/* §3.2 — typed deliverables alternative to the single-bytes32 path.
                The contract sets stageProof := keccak256(abi.encode(deliverables))
                so a typed submission acts as the anchor for the same milestone. */}
            <TypedDeliverablesPanel
              deliverables={deliverables}
              setDeliverables={setDeliverables}
              submitting={submitting}
              onSubmit={(rows) => doWrite({
                fn: 'submitDeliverables',
                args: [
                  BigInt(id),
                  rows.map((d) => [d.dtype, toBytes32(d.evidenceHash) ?? '0x' + '0'.repeat(64), d.uri]),
                ],
                pendingTitle: 'Submitting typed deliverables…',
                successTitle: `Submitted ${rows.length} deliverable${rows.length === 1 ? '' : 's'}`,
              })()}
            />
          </>
        )}
        {labCanResubmit && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <input
              className="mono"
              style={{ flex: 1, padding: '6px 10px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
              placeholder="new proof reference"
              value={resubmitText}
              onChange={(e) => setResubmitText(e.target.value)}
            />
            <button className={`btn ${resubmitBytes32 && !submitting ? 'btn-prim' : 'btn-locked'}`}
                    disabled={!resubmitBytes32 || submitting}
                    onClick={doWrite({ fn: 'resubmitProof', args: [BigInt(id), resubmitBytes32 ?? '0x' + '0'.repeat(64)], pendingTitle: 'Resubmitting proof…', successTitle: 'Proof resubmitted' })}>
              Resubmit proof
            </button>
          </div>
        )}
      </div>

      {/* AUDITOR */}
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Pill tone="purple">Auditor</Pill>
          <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            {p.isAuditorForThis ? 'isAuditorFor(this, you) === true'
              : p.isActiveAuditor ? 'isActiveAuditor(you) === true · not assigned'
              : 'visible only to active auditors'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <button className={`btn ${audCanApply && !submitting ? 'btn-prim' : 'btn-locked'}`}
                  disabled={!audCanApply || submitting}
                  style={audCanApply && !submitting ? { background: 'var(--purple)', borderColor: 'var(--purple)' } : undefined}
                  onClick={doWrite({ fn: 'applyForAudit', args: [BigInt(id)], pendingTitle: 'Applying as auditor…', successTitle: 'Applied to audit' })}>
            Apply for audit
          </button>
          <button className={`btn ${audCanReview && !submitting ? 'btn-prim' : 'btn-locked'}`}
                  disabled={!audCanReview || submitting}
                  style={audCanReview && !submitting ? { background: 'var(--purple)', borderColor: 'var(--purple)' } : undefined}
                  onClick={doWrite({ fn: 'reviewStageProof', args: [BigInt(id), true], pendingTitle: 'Approving proof…', successTitle: 'Proof approved' })}>
            Approve proof
          </button>
          <button className={`btn ${audCanReview && !submitting ? '' : 'btn-locked'}`}
                  disabled={!audCanReview || submitting}
                  onClick={doWrite({ fn: 'reviewStageProof', args: [BigInt(id), false], pendingTitle: 'Rejecting proof…', successTitle: 'Proof rejected' })}>
            Reject proof
          </button>
          <button className={`btn ${!submitting ? '' : 'btn-locked'}`}
                  disabled={submitting}
                  onClick={doWrite({ fn: 'checkExpiredDispute', args: [BigInt(id)], pendingTitle: 'Checking dispute timer…', successTitle: 'Dispute resolved' })}>
            Check expired
          </button>
        </div>

        {audCanCastVote && (
          <div style={{ marginTop: 8, padding: 12, background: 'var(--panel-2)', borderRadius: 5 }}>
            <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
              Cast verdict vote (Condorcet-averaged amounts)
            </div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 8 }}>
              <label className="mono" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={voteApprove === true} onChange={() => setVoteApprove(true)} />
                Approve
              </label>
              <label className="mono" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={voteApprove === false} onChange={() => setVoteApprove(false)} />
                Reject
              </label>
              <span className="mono dim" style={{ fontSize: 11 }}>Score</span>
              <input
                className="mono"
                style={{ width: 60, padding: '4px 8px', fontSize: 11, background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
                value={voteScore}
                onChange={(e) => setVoteScore(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0–100"
              />
            </div>
            <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>Milestone amounts (ETH each)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6, marginBottom: 8 }}>
              {voteAmounts.map((val, idx) => (
                <input
                  key={idx}
                  className="mono"
                  style={{ padding: '4px 8px', fontSize: 11, background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
                  value={val}
                  onChange={(e) => {
                    const next = [...voteAmounts];
                    next[idx] = e.target.value.replace(/[^0-9.]/g, '');
                    setVoteAmounts(next);
                  }}
                  placeholder="0.0"
                />
              ))}
            </div>
            <button
              className={`btn btn-prim btn-sm${submitting ? ' btn-locked' : ''}`}
              style={{ background: 'var(--purple)', borderColor: 'var(--purple)' }}
              disabled={submitting}
              onClick={() => {
                // Parse + validate amounts; bail on failure.
                let weiAmounts: bigint[];
                try {
                  weiAmounts = voteAmounts.map((s) => ethers.parseEther(s || '0'));
                } catch {
                  return;
                }
                const score = Math.min(100, Math.max(0, parseInt(voteScore || '0', 10) || 0));
                return write({
                  contract: 'auditingPool',
                  fn: 'castVote',
                  args: [BigInt(id), voteApprove, score, weiAmounts],
                  options: {
                    pendingTitle: 'Casting verdict vote…',
                    successTitle: `Voted ${voteApprove ? 'approve' : 'reject'} on #${id}`,
                    invalidate: [['metha', 'auditingPool']],
                  },
                });
              }}
            >
              {submitting ? 'Submitting…' : 'Cast vote'}
            </button>
          </div>
        )}
      </div>

      {/* DSMB — §3.1 patient-safety veto, gated by AuditorRegistryV2.hasDsmbVeto */}
      {p.userHasDsmbVeto && (
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)', background: canDsmbVeto ? 'rgba(255,80,80,.05)' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Pill tone="amber">DSMB</Pill>
            <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
              hasDsmbVeto(you) === true · safety lane
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className={`btn ${canDsmbVeto && !submitting ? 'btn-prim' : 'btn-locked'}`}
              disabled={!canDsmbVeto || submitting}
              style={canDsmbVeto && !submitting ? { background: 'var(--red)', borderColor: 'var(--red)' } : undefined}
              onClick={doWrite({
                fn: 'dsmbVeto',
                args: [BigInt(id), BigInt(p.currentMilestoneIdx)],
                pendingTitle: `Vetoing M${p.currentMilestoneIdx + 1} on patient-safety grounds…`,
                successTitle: `DSMB veto recorded on M${p.currentMilestoneIdx + 1}`,
              })}
              title={
                !p.isClinicalMilestone ? 'Only ClinicalData milestones can be vetoed' :
                p.milestoneAlreadyVetoed ? 'This milestone is already vetoed' :
                !p.verdictAccepted ? 'Verdict not accepted yet — nothing to veto' :
                p.cancelled ? 'Request cancelled' :
                undefined
              }
            >
              DSMB veto current milestone
            </button>
            <span className="mono dim" style={{ fontSize: 11 }}>
              {p.milestoneAlreadyVetoed
                ? 'already vetoed — lab can resubmit to clear'
                : p.isClinicalMilestone
                  ? 'forces Disputed; lab can resubmit (clears veto for one remediation pass)'
                  : 'milestone is not typed as ClinicalData — veto disabled'}
            </span>
          </div>
        </div>
      )}

      {/* FAILURE / DEPOSIT MANAGEMENT */}
      <div style={{ padding: '16px 18px', background: labCanReclaim ? 'var(--amber-soft)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Pill tone="amber">Failure modes</Pill>
          <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            {labCanReclaim ? 'available now — claim or burn deposit' : 'visible only when applicable'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${labCanReclaim && !submitting ? 'btn-prim' : 'btn-locked'}`}
                  disabled={!labCanReclaim || submitting}
                  onClick={doWrite({ fn: 'claimRequestDeposit', args: [BigInt(id)], pendingTitle: 'Claiming deposit…', successTitle: 'Deposit reclaimed' })}>
            Reclaim 0.005 ETH deposit
          </button>
          <button className={`btn btn-sm ${p.cancelled && !submitting ? '' : 'btn-locked'}`}
                  disabled={!p.cancelled || submitting}
                  onClick={doWrite({ fn: 'burnRequestDeposit', args: [BigInt(id)], pendingTitle: 'Burning deposit to vault…', successTitle: 'Deposit donated to vault' })}>
            Burn deposit (→ vault)
          </button>
        </div>
        <p className="help mt-14" style={{ marginBottom: 0, color: 'var(--dim)' }}>
          Note: v3 removed <span className="mono">yieldFunds</span> — labs cannot unilaterally
          cancel post-acceptance. Only the dispute timeout path or rejectVerdict cancels.
        </p>
      </div>
    </div>
  );
}

/** Coerce a free-text proof input to a bytes32 hex string.
 *  Accepts either a 0x-prefixed bytes32 (returned verbatim) or arbitrary text
 *  (returned as ASCII-padded bytes32 via ethers.encodeBytes32String). Returns
 *  null if the input is empty or doesn't fit in 32 bytes.
 */
function toBytes32(s: string): string | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  if (/^0x[0-9a-fA-F]{64}$/.test(trimmed)) return trimmed;
  try {
    return ethers.encodeBytes32String(trimmed);
  } catch {
    return null;
  }
}

/* -------------------- Typed deliverables panel (§3.2) -------------------- */

function TypedDeliverablesPanel(p: {
  deliverables: Array<{ dtype: number; uri: string; evidenceHash: string }>;
  setDeliverables: Dispatch<SetStateAction<Array<{ dtype: number; uri: string; evidenceHash: string }>>>;
  submitting: boolean;
  onSubmit: (rows: Array<{ dtype: number; uri: string; evidenceHash: string }>) => void;
}) {
  const update = (i: number, patch: Partial<{ dtype: number; uri: string; evidenceHash: string }>) =>
    p.setDeliverables((arr) => arr.map((d, j) => (i === j ? { ...d, ...patch } : d)));
  const removeRow = (i: number) =>
    p.setDeliverables((arr) => arr.filter((_, j) => j !== i));
  const addRow = () =>
    p.setDeliverables((arr) => [...arr, { dtype: 0, uri: '', evidenceHash: '' }]);

  // A row is "submittable" once it has both a URI and an evidence hash that
  // parses to bytes32. URI alone is not enough — the on-chain anchor lives in
  // evidenceHash. Contract caps at MAX_DELIVERABLES_PER_MILESTONE (10).
  const ready = p.deliverables.length > 0 &&
    p.deliverables.length <= 10 &&
    p.deliverables.every((d) => d.uri.trim().length > 0 && toBytes32(d.evidenceHash) !== null);

  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--panel-2)', borderRadius: 5, border: '1px solid var(--line)' }}>
      <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Or: typed deliverables (§3.2) · <code>submitDeliverables(reqId, Deliverable[])</code></span>
        <span style={{ color: p.deliverables.length > 10 ? 'var(--red)' : 'var(--dim)' }}>
          {p.deliverables.length} / 10
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        {p.deliverables.map((d, i) => (
          <div key={i} className="deliv-row">
            <select
              value={d.dtype}
              onChange={(e) => update(i, { dtype: Number(e.target.value) })}
              className="mono"
              style={{ padding: '4px 8px', fontSize: 11, background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
              title="DeliverableType enum (AuditingPool.sol L105)"
            >
              {DELIVERABLE_TYPES.map((t, idx) => (
                <option key={t} value={idx} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>{t}</option>
              ))}
            </select>
            <input
              className="mono"
              style={{ padding: '4px 8px', fontSize: 11, background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
              placeholder="uri (ipfs://… or https://…)"
              value={d.uri}
              onChange={(e) => update(i, { uri: e.target.value })}
            />
            <input
              className="mono"
              style={{ padding: '4px 8px', fontSize: 11, background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
              placeholder="evidence hash (text or 0x-prefixed bytes32)"
              value={d.evidenceHash}
              onChange={(e) => update(i, { evidenceHash: e.target.value })}
            />
            <button
              className="btn btn-sm"
              onClick={() => removeRow(i)}
              disabled={p.deliverables.length === 1}
              title={p.deliverables.length === 1 ? 'At least one deliverable required' : 'Remove deliverable'}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="btn btn-sm" onClick={addRow} disabled={p.deliverables.length >= 10}>
          + Add deliverable
        </button>
        <button
          className={`btn btn-sm ${ready && !p.submitting ? 'btn-prim' : 'btn-locked'}`}
          disabled={!ready || p.submitting}
          onClick={() => ready && p.onSubmit(p.deliverables)}
        >
          {p.submitting ? 'Submitting…' : `Submit ${p.deliverables.length} typed deliverable${p.deliverables.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

/* -------------------- Review progress bar -------------------- */

function ReviewProgressBar({ approveCount, rejectCount, total }: { approveCount: number; rejectCount: number; total: number }) {
  const threshold = Math.floor(total / 2) + 1; // approveCount * 2 > total
  const approvePct = total > 0 ? (approveCount / total) * 100 : 0;
  const rejectPct  = total > 0 ? (rejectCount / total) * 100 : 0;
  const pending = total - approveCount - rejectCount;
  return (
    <>
      <div style={{
        display: 'flex',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        background: 'var(--line)',
        marginBottom: 8,
      }}>
        <div style={{ width: `${approvePct}%`, background: 'var(--green)' }} />
        <div style={{ width: `${rejectPct}%`,  background: 'var(--red)' }} />
      </div>
      <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span><b style={{ color: 'var(--green)' }}>{approveCount}</b> approve</span>
        <span><b style={{ color: 'var(--red)' }}>{rejectCount}</b> reject</span>
        <span>{pending} pending</span>
        <span>· threshold {threshold} of {total}</span>
      </div>
    </>
  );
}

